import { createSharedComposable } from '@vueuse/core'
import { type AutoQaFeedback } from '@zendesk/zqa-services/autoqa'
import { ref } from 'vue'

export enum OmniPanelView {
  REVIEW = 'review',
  FEEDBACK = 'feedback',
  INFO = 'info',
  UNDEFINED = '',
}

export enum PanelViewSection {
  Manual = 'manual',
  AutoQa = 'autoqa',
}

const REVIEWS_EXPANDED_STORAGE_KEY = 'zqaAllReviewsExpanded.v1'
const AUTOQA_REVIEWS_EXPANDED_STORAGE_KEY = 'zqaAllAutoQaReviewsExpanded.v1'

export default createSharedComposable(() => {
  const panelView = ref(OmniPanelView.FEEDBACK)
  const allReviewPanelsOpen = ref<boolean>(JSON.parse(localStorage.getItem(REVIEWS_EXPANDED_STORAGE_KEY)) || false)
  const allAutoQaReviewPanelsOpen = ref<boolean>(
    JSON.parse(localStorage.getItem(AUTOQA_REVIEWS_EXPANDED_STORAGE_KEY)) || false,
  )

  const activeFeedbackView = (routeName: string) => {
    if (routeName.includes('message')) {
      return 'message'
    } else if (routeName.endsWith('.dispute')) {
      return 'dispute'
    } else if (routeName.endsWith('.evaluations')) {
      return 'evaluations'
    }
    return undefined
  }

  const setPanelView = (view: OmniPanelView) => {
    panelView.value = view
  }

  const toggleReviewPanel = () => {
    const view = panelView.value === OmniPanelView.REVIEW ? undefined : OmniPanelView.REVIEW
    setPanelView(view)
  }

  const openCollapsibleItemsIds = ref([] as string[])

  const openCollapsiblePanel = (id: string) => {
    if (openCollapsibleItemsIds.value.includes(id)) {
      removeIdFromOpenCollapsibleItems(id)
    } else {
      addIdToOpenCollapsibleItems(id)
    }
  }

  function syncStorageOnManualPanelToggle(section: PanelViewSection, allIds: string[]) {
    const allOpen = allIds.length > 0 && allIds.every((id) => openCollapsibleItemsIds.value.includes(id))
    const allClosed = allIds.every((id) => !openCollapsibleItemsIds.value.includes(id))

    if (!allOpen && !allClosed) return

    if (section === PanelViewSection.Manual) {
      localStorage.setItem(REVIEWS_EXPANDED_STORAGE_KEY, String(allOpen))
      allReviewPanelsOpen.value = allOpen
    } else if (section === PanelViewSection.AutoQa) {
      localStorage.setItem(AUTOQA_REVIEWS_EXPANDED_STORAGE_KEY, String(allOpen))
      allAutoQaReviewPanelsOpen.value = allOpen
    }
  }

  function openCollapsiblePanelWithSync(id: string, section: PanelViewSection, allIds: string[]) {
    openCollapsiblePanel(id)
    if (section) syncStorageOnManualPanelToggle(section, allIds)
  }

  const isCondensedItemPanelCollapsed = (id: string) => {
    return openCollapsibleItemsIds.value.includes(id)
  }

  const addIdToOpenCollapsibleItems = (id: string) => {
    openCollapsibleItemsIds.value.push(id)
  }

  const removeIdFromOpenCollapsibleItems = (id: string) => {
    openCollapsibleItemsIds.value.splice(openCollapsibleItemsIds.value.indexOf(id), 1)
  }

  const itemWithIdIsOpen = (id: string) => {
    return openCollapsibleItemsIds.value.includes(id)
  }

  const toggleAllReviewPanels = (ids: string[]) => {
    const willExpand = !allReviewPanelsOpen.value
    const handler = willExpand ? addIdToOpenCollapsibleItems : removeIdFromOpenCollapsibleItems
    ids.filter((id) => itemWithIdIsOpen(id) !== willExpand).forEach(handler)
    localStorage.setItem(REVIEWS_EXPANDED_STORAGE_KEY, String(willExpand))
    allReviewPanelsOpen.value = willExpand
  }

  const toggleAllAutoQaReviewPanels = (ids: string[]) => {
    const willExpand = !allAutoQaReviewPanelsOpen.value
    const handler = willExpand ? addIdToOpenCollapsibleItems : removeIdFromOpenCollapsibleItems
    ids.filter((id) => itemWithIdIsOpen(id) !== willExpand).forEach(handler)
    localStorage.setItem(AUTOQA_REVIEWS_EXPANDED_STORAGE_KEY, String(willExpand))
    allAutoQaReviewPanelsOpen.value = willExpand
  }

  const getAutoQaFeedbackPanelId = (feedback: AutoQaFeedback) => {
    if (!feedback) return null
    if (feedback.transcriptionId) return feedback.transcriptionId
    return feedback.revieweeName || feedback.botRevieweeName
  }

  return {
    panelView,
    activeFeedbackView,
    setPanelView,
    toggleReviewPanel,
    openCollapsiblePanel,
    isCondensedItemPanelCollapsed,
    allReviewPanelsOpen,
    allAutoQaReviewPanelsOpen,
    toggleAllReviewPanels,
    toggleAllAutoQaReviewPanels,
    openCollapsibleItemsIds,
    getAutoQaFeedbackPanelId,
    openCollapsiblePanelWithSync,
  }
})
